import { Base } from "./Base";
import { initBehaviors, destroyBehaviors } from "..";
import breakpoints from "../../constants/breakpoints";
import events from "../../constants/events";
import { dispatchEvent } from "../../utilities/customEvents";
import { relewiseProductsSearch } from "../../utilities/relewise";
import compileProductCard from "../../utilities/compileProductCard";
import { getShopifyProductsByGlobalIds } from "../../utilities/getShopifyProductsByGlobalIds";
import { getElement } from "@egjs/flicking";

const replaceFromArray = (array, string) => {
  if (!array) return string;

  return array.reduce((acc, cur) => {
    const caseInsensitive = (str) => new RegExp(str, "ig");
    const combos = [`${cur} `, ` ${cur}`];

    return combos.reduce((acc2, cur2) => acc2.replaceAll(caseInsensitive(cur2), ""), acc);
  }, string);
};

export class SearchResults extends Base {
  constructor(behaviorName, element) {
    super(behaviorName, element);
    this.theme = null;
    this.isIndex = false;
    this.timeout = this.init();
  }

  init() {
    const { theme, isIndex } = this.element.dataset;
    this.theme = theme;
    this.isIndex = isIndex == "true" ? true : false;
    this.openSearchHandler = this.openSearch.bind(this);
    window.addEventListener(events.OPEN_SEARCH, this.openSearchHandler);
    this.closeSearchHandler = this.closeSearch.bind(this);
    window.addEventListener(events.CLOSE_SEARCH, this.closeSearchHandler);
    this.updateSearchHandler = this.updateSearch.bind(this);
    window.addEventListener(events.UPDATE_SEARCH, this.updateSearchHandler);
    //
    const { overlay } = this.refs;
    overlay.addEventListener("click", (e) => {
      dispatchEvent(window, events.CLOSE_SEARCH, null);
    });
  }

  openSearch(e) {
    this.hideWrappers();
    this.updateSearch(e);
  }

  closeSearch(e) {
    this.hideWrappers();
    this.hideAll();
  }

  applyDataModelling(data) {
    const modeledData = [];

    data.forEach((entry) => {
      if (entry.productId && entry.variant) {
        modeledData.push({
          crossStoreProductId: isNaN(parseInt(entry.productId)) ? entry.productId : parseInt(entry.productId),
          variantId: isNaN(parseInt(entry.variant.variantId))
            ? entry.variant.variantId
            : parseInt(entry.variant.variantId),
        });
      } else {
        console.warn("Expects both product and variant id");
      }
    });
    return modeledData;
  }

  hideWrappers() {
    const { inner, predictionsWrapper, productsWrapper, categoriesWrapper, redirectsWrapper } = this.refs;
    inner.classList.remove("display");
    predictionsWrapper.classList.remove("display");
    productsWrapper.classList.remove("display");
    categoriesWrapper.classList.remove("display");
    redirectsWrapper.classList.remove("display");
  }

  hideAll() {
    const {
      predictionsWrapper,
      categoriesWrapper,
      redirectsWrapper,
      predictionsList,
      categoriesList,
      redirectsList,
      productsWrapper,
      inner,
      totalCount,
      totalSingular,
      totalPlural,
      viewAll,
      viewAllButton,
    } = this.refs;

    totalCount.classList.remove("display");
    totalSingular.classList.remove("display");
    totalPlural.classList.remove("display");
    viewAll.classList.remove("display");
    predictionsList.innerHTML = "";
    categoriesList.innerHTML = "";
    redirectsList.innerHTML = "";
    productsWrapper.innerHTML = "";
  }

  updateSearch(e) {
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.doUpdateSearch(e);
    }, 500);
  }

  async doUpdateSearch(e) {
    const {
      predictionsWrapper,
      categoriesWrapper,
      redirectsWrapper,
      predictionsList,
      categoriesList,
      redirectsList,
      productsWrapper,
      inner,
      totalCount,
      totalSingular,
      totalPlural,
      viewAll,
      viewAllButton,
      noResults,
    } = this.refs;
    const vendor = this.isIndex ? "all" : this.theme == "theme-man" ? "Gallery" : "Mos Mosh";

    const { key } = e.detail;
    const max = 12;
    const searcher = relewiseProductsSearch(key, "search header", vendor, max);
    const products = await searcher.products;

    // Filter redirects
    const allRedirects = products.redirects || [];
    const productRedirects = [];
    const otherRedirects = [];

    allRedirects.forEach((r) => {
      if (r.destination && r.destination.includes("/products/")) {
        productRedirects.push(r);
      } else {
        otherRedirects.push(r);
      }
    });

    const fetchedRedirectProducts = [];
    if (productRedirects.length > 0) {
      await Promise.all(
        productRedirects.map(async (redirect) => {
          try {
            let urlObj;
            try {
              urlObj = new URL(redirect.destination);
            } catch (e) {
              urlObj = new URL(redirect.destination, window.location.origin);
            }
            
            const pathParts = urlObj.pathname.split("/");
            const handleIndex = pathParts.indexOf("products");
            const handle = handleIndex !== -1 ? pathParts[handleIndex + 1] : null;

            if (handle) {
              const res = await fetch(`/products/${handle}?view=id`);
              if (res.ok) {
                const html = await res.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");
                const div = doc.querySelector("div[data-product-id]");

                if (div) {
                  fetchedRedirectProducts.push({
                    productId: div.dataset.productId,
                    variant: { variantId: div.dataset.variantId },
                  });
                }
              }
            }
          } catch (e) {
            console.warn("Error fetching product redirect", e);
          }
        })
      );
    }

    const regularProductsLimit = max - fetchedRedirectProducts.length;
    const combinedResults = [...fetchedRedirectProducts, ...products.results.slice(0, Math.max(0, regularProductsLimit))];

    const { productCardTemplate, productCardMediaItemTemplate, swatchTemplate, swatchItemTemplate } = this.refs;

    this.hideAll();
    if (key.length <= 0) {
      this.hideWrappers();
    }

    const gotNoResults = key.length > 0 && products.hits === 0 && products.redirects === undefined;
    noResults.classList.toggle("display", gotNoResults);

    const templates = {
      productCardTemplate,
      productCardMediaItemTemplate,
      swatchTemplate,
      swatchItemTemplate,
    };

    const settings = {
      hideTags: false,
      showPackshot: false,
      preselectURL: true,
      showVideoHover: true,
    };

    const shopifyProducts = getShopifyProductsByGlobalIds(
      this.applyDataModelling(combinedResults),
      combinedResults.some(
        (entry) => isNaN(parseInt(entry.crossStoreProductId)) || isNaN(parseInt(entry.variant.variantId))
      )
    );
    
    productsWrapper.innerHTML = "";
    productsWrapper.classList.remove("display");

    const getFirstAvailableVariant = (variants) => {
      return variants.find((variant) => variant.available) || variants[0];
    };

    const shopifyProductsSorted = shopifyProducts.sort((a, b) => {
      const aIndex = combinedResults.findIndex((entry) => 
        entry.productId == a.crossStoreProductId || entry.productId == a.id
      );
      const bIndex = combinedResults.findIndex((entry) => 
        entry.productId == b.crossStoreProductId || entry.productId == b.id
      );
      return aIndex - bIndex;
    });

    for (const product of shopifyProductsSorted) {
      const firstAvailableVariant = getFirstAvailableVariant(product.variants);
      const compiledProductCard = compileProductCard(product, firstAvailableVariant, templates, settings);

      if (compiledProductCard) {
        productsWrapper.innerHTML += `<div class="search-results-product-card ss-grid__item ss-grid__item--4-of-8 ss-grid__item--2-of-8-md grid_product_2">
        ${compiledProductCard}
        </div>`;
      } else {
        productsWrapper.innerHTML += `<div class="search-results-product-card"><div class="search-results-product-card_info">${productData}</div></div>`;
      }
    }

    totalCount.classList.remove("display");
    totalSingular.classList.remove("display");
    totalPlural.classList.remove("display");
    viewAll.classList.remove("display");

    const gender = this.theme == "theme-man" ? "gallery" : "mos-mosh";
    viewAllButton.href = "/search?type=product&q=" + key + "&gender=" + gender;

    if (products && products.hits) {
      //const pre = products.hits > max ? max + " / " : "";
      const pre = "";
      totalCount.innerHTML = pre + products.hits;
      totalCount.classList.add("display");
      if (products.hits == 1) {
        totalSingular.classList.add("display");
      } else {
        totalPlural.classList.add("display");
      }
      if (products.hits > max) {
        viewAll.classList.add("display");
      }
    }

    const predictions = await searcher.predictions;

    predictionsList.innerHTML = "";
    if (predictionsWrapper && predictions && predictions.predictions.length > 0) {
      predictionsWrapper.classList.add("display");

      for (const prediction of predictions.predictions) {
        const term = prediction.term;
        var element = document.createElement("li");
        element.onclick = function () {
          dispatchEvent(window, events.SELECT_PREDICTION_SEARCH, {
            term: term,
          });
        };
        element.innerHTML = term;

        predictionsList.appendChild(element);
      }
    } else {
      predictionsWrapper.classList.remove("display");
    }

    const categories = await searcher.categories;
    categoriesList.innerHTML = "";
    if (categoriesWrapper && categories && categories.results.length > 0) {
      categoriesWrapper.classList.add("display");

      for (const category of categories.results) {
        const id = category.categoryId;
        var element = document.createElement("li");
        var link = document.createElement("a");
        if (category.data && category.data.Handle && category.data.Handle.value) {
          link.setAttribute("href", "/collections/" + category.data.Handle.value);
        }

        // remove certain parts of category names ("to men" fex.)
        const { replaceStrings } = this.element.dataset;
        link.innerHTML = replaceFromArray(replaceStrings?.split(","), category.displayName);
        element.appendChild(link);
        categoriesList.appendChild(element);
      }
    } else {
      categoriesWrapper.classList.remove("display");
    }

    const redirectsArray = otherRedirects;
    redirectsList.innerHTML = "";
    if (
      redirectsWrapper &&
      redirectsArray &&
      redirectsArray.length > 0 &&
      redirectsArray.some((redirect) => redirect?.data?.name || redirect?.destination)
    ) {
      redirectsWrapper.classList.add("display");

      for (const redirect of redirectsArray) {
        if (!redirect?.data?.name || !redirect?.destination) continue;

        var element = document.createElement("li");
        var link = document.createElement("a");

        if (redirect.destination) {
          link.setAttribute("href", redirect.destination);
        }

        function formatFallbackTitle(title) {
          return (
            title.split("/").pop().replace(/-/g, " ").charAt(0).toUpperCase() +
            redirect.destination.split("/").pop().replace(/-/g, " ").slice(1)
          );
        }

        if (redirect?.data?.name) {
          link.innerHTML = redirect.data.name;
        } else if (redirect?.destination) {
          link.innerHTML = formatFallbackTitle(redirect.destination);
        }
        element.appendChild(link);
        redirectsList.appendChild(element);
      }
    } else {
      redirectsWrapper.classList.remove("display");
    }

    if (
      (predictions && predictions.predictions.length > 0) ||
      (shopifyProducts && shopifyProducts.length >= 1) ||
      redirectsArray
    ) {
      dispatchEvent(window, events.PRODUCT_IMAGES_UPDATED);
      inner.classList.add("display");
      setTimeout(() => {
        productsWrapper.classList.add("display");
        destroyBehaviors(productsWrapper);
        initBehaviors(productsWrapper);
      }, 400);
    } else {
      this.hideWrappers();
    }
  }

  cleanup() {
    if (this.timeout) clearTimeout(this.timeout);
    window.removeEventListener(events.OPEN_SEARCH, this.openSearchHandler);
    window.removeEventListener(events.CLOSE_SEARCH, this.closeSearchHandler);
    window.removeEventListener(events.UPDATE_SEARCH, this.updateSearchHandler);
  }
}
